import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';
import { Box, Icon } from 'roo-ui/components';
import noop from 'lodash/noop';
import { Label, LabelText } from 'components/Label';
import TextHighlighter from 'components/TextHighlighter';
import ResponsiveModal from 'components/ResponsiveModal';
import { useLocationData } from './useLocationData';
import { AutocompleteInput, ResultItem, ResultList } from './primitives';

const PLACEHOLDER_TEXT = 'Destination, city, hotel name';

const isLocation = (item) => item?.type === 'location';
const isProperty = (item) => item?.type === 'property';

const itemToString = (item) => (item ? item.fullName : '');

const LocationAutocompleter = ({ locationName, labelOptions, routeToProperty, updateQuery }) => {
  const [items, setItems] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  /* Because we are combining render props with hooks, we need to store these functions as refs so they are defined in the hook and assigned in the render */
  const blurModalRef = useRef(noop);
  const openModalRef = useRef(noop);
  const inputRef = useRef();

  const stateReducer = useCallback((state, actionAndChanges) => {
    const { type, changes } = actionAndChanges;

    switch (type) {
      case useCombobox.stateChangeTypes.InputBlur:
        return { ...changes, inputValue: '' };
      default:
        return changes;
    }
  }, []);

  const onSelectedItemChange = useCallback(
    ({ selectedItem }) => {
      blurModalRef.current();
      inputRef.current.blur();
      // Clear subregions when the location is changed - don't want to apply Melbourne subregions to a Brisbane location search
      if (isLocation(selectedItem)) updateQuery({ location: selectedItem.fullName, subRegions: undefined });
      if (isProperty(selectedItem)) routeToProperty({ id: selectedItem.id, excludeParams: ['location'] });
    },
    [routeToProperty, updateQuery],
  );

  const { isOpen, highlightedIndex, getComboboxProps, getLabelProps, getInputProps, getMenuProps, getItemProps, inputValue } = useCombobox({
    items,
    itemToString,
    stateReducer,
    onSelectedItemChange,
    defaultHighlightedIndex: 0,
    inputId: 'location-search-input',
    labelId: 'location-search-label',
  });

  const results = useLocationData(inputValue);
  useEffect(() => {
    setItems(results);
  }, [results]);

  const onFocus = useCallback(() => {
    openModalRef.current();
    setIsFocused(true);
  }, []);

  const onBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <ResponsiveModal title="Find destination or hotel">
      {({ openModal, blurModal }) => {
        blurModalRef.current = blurModal;
        openModalRef.current = openModal;
        return (
          <Box position="relative">
            <Label {...getLabelProps()}>
              <LabelText {...labelOptions} hidden={[true, false]} color="greys.charcoal" fontWeight="normal" fontSize="sm">
                Destination or hotel name
              </LabelText>
              <Box {...getComboboxProps()}>
                <AutocompleteInput
                  {...getInputProps({
                    ref: inputRef,
                    left: 2,
                    borderRadius: isOpen ? 'defaultRoundTopOnly' : 'default',
                    border: ['1', '2', '2'],
                    placeholder: isFocused ? PLACEHOLDER_TEXT : locationName,
                    icon: 'search',
                    isFocused,
                    onFocus,
                    onBlur,
                    'data-testid': 'location-search-input',
                  })}
                />
              </Box>
            </Label>
            <ResultList isOpen={isOpen} {...getMenuProps()}>
              {results.map((item, index) => (
                <ResultItem
                  key={index}
                  data-testid="location-search-result"
                  {...getItemProps({
                    index,
                    item,
                    highlighted: highlightedIndex === index,
                  })}
                >
                  <Box>
                    {isLocation(item) && <Icon name="place" mr={2} ml="-8px" />}
                    {isProperty(item) && <Icon name="localHotel" mr={2} ml="-8px" />}
                  </Box>
                  <Box>
                    <TextHighlighter text={item.fullName} highlightText={inputValue} />
                  </Box>
                </ResultItem>
              ))}
            </ResultList>
          </Box>
        );
      }}
    </ResponsiveModal>
  );
};

LocationAutocompleter.propTypes = {
  locationName: PropTypes.string,
  labelOptions: PropTypes.object,
  updateQuery: PropTypes.func.isRequired,
  routeToProperty: PropTypes.func.isRequired,
};

LocationAutocompleter.defaultProps = {
  locationName: '',
  labelOptions: {},
};

export default LocationAutocompleter;
